import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiPhone, FiMail, FiPrinter, FiEdit2, FiCheck, FiX, FiTruck, FiExternalLink, FiRefreshCw, FiTrash2, FiCopy, FiPlus, FiMinus } from 'react-icons/fi';
import { adminApi } from '../adminApi';
import { showConfirm, showAlert } from '../utils/dialog';

const STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled', 'return'];

const STATUS_STYLE = {
  pending:   { bg: '#fef9c3', color: '#854d0e' },
  confirmed: { bg: '#dbeafe', color: '#1e40af' },
  shipped:   { bg: '#dbeafe', color: '#1e40af' },
  delivered: { bg: '#dcfce7', color: '#166534' },
  cancelled: { bg: '#f1f5f9', color: '#475569' },
};

function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order,        setOrder]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [editing,      setEditing]      = useState(false);
  const [draft,        setDraft]        = useState({});
  const [tracking,     setTracking]     = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [comments,     setComments]     = useState([]);
  const [commentText,  setCommentText]  = useState('');
  const [commentSaving,setCommentSaving]= useState(false);
  const [cancelModal,  setCancelModal]  = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [draftItems,   setDraftItems]   = useState([]);
  const [allProducts,  setAllProducts]  = useState([]);
  const [productSearch,setProductSearch]= useState('');
  const [showPicker,   setShowPicker]   = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    adminApi.getOrder(id).then(r => {
      setOrder(r.data);
      setComments(r.data.comments || []);
      if (r.data.postex_tracking) fetchTracking(r.data.id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const fetchTracking = (orderId) => {
    setTrackLoading(true);
    adminApi.getOrderTracking(orderId)
      .then(r => setTracking(r.data))
      .catch(() => setTracking(null))
      .finally(() => setTrackLoading(false));
  };

  const handleStatus = async (status) => {
    setSaving(true);
    await adminApi.updateOrderStatus(id, status).catch(() => {});
    const r = await adminApi.getOrder(id).catch(() => null);
    if (r) setOrder(r.data);
    setSaving(false);
  };

  const startEdit = () => {
    setDraft({
      guest_name:       order.guest_name || '',
      guest_email:      order.guest_email || '',
      phone:            order.phone || '',
      delivery_address: order.delivery_address || '',
      shipping_amount:  order.shipping_amount ?? 200,
    });
    setDraftItems(order.items.map(i => ({
      product_id: i.product_id,
      name:       i.name,
      image_url:  i.image_url,
      price:      i.price,
      quantity:   i.quantity,
    })));
    adminApi.getProducts().then(r => setAllProducts(r.data)).catch(() => {});
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const openFulfillPage = () => {
    navigate('/orders/book', { state: { orderIds: [parseInt(id)] } });
  };

  const [editingComment, setEditingComment] = useState(null); // { id, text }

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setCommentSaving(true);
    try {
      const res = await adminApi.addOrderComment(id, { text });
      setComments(prev => [...prev, res.data]);
      setCommentText('');
    } catch {}
    finally { setCommentSaving(false); }
  };

  const saveEditComment = async () => {
    if (!editingComment?.text.trim()) return;
    try {
      const res = await adminApi.updateOrderComment(id, editingComment.id, { text: editingComment.text.trim() });
      setComments(prev => prev.map(c => c.id === editingComment.id ? res.data : c));
      setEditingComment(null);
    } catch {}
  };

  const deleteComment = async (cid) => {
    try {
      await adminApi.deleteOrderComment(id, cid);
      setComments(prev => prev.filter(c => c.id !== cid));
    } catch {}
  };

  const handleCancelOrder = async () => {
    setSaving(true);
    try {
      if (cancelReason.trim()) {
        const res = await adminApi.addOrderComment(id, { text: `Cancellation reason: ${cancelReason.trim()}` });
        setComments(prev => [...prev, res.data]);
      }
      await adminApi.updateOrderStatus(id, 'cancelled');
      const r = await adminApi.getOrder(id);
      setOrder(r.data);
      setCancelModal(false);
      setCancelReason('');
    } catch {}
    finally { setSaving(false); }
  };

  const handleStatusChange = (status) => {
    if (status === 'cancelled') { setCancelModal(true); return; }
    handleStatus(status);
  };

  const saveEdit = async () => {
    if (draftItems.length === 0) { showAlert('Order must have at least one item.'); return; }
    setSaving(true);
    try {
      await adminApi.updateOrder(id, draft);
      await adminApi.updateOrderItems(id, { items: draftItems });
      const r = await adminApi.getOrder(id);
      setOrder(r.data);
      setComments(r.data.comments || []);
      setEditing(false);
    } catch (err) {
      showAlert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const duplicateOrder = async () => {
    if (!await showConfirm(`Duplicate order #${order.id}? A new pending order will be created.`, 'Duplicate Order')) return;
    try {
      const res = await adminApi.duplicateOrder(id);
      navigate(`/orders/${res.data.id}`);
    } catch { showAlert('Failed to duplicate order'); }
  };

  const updItem = (idx, field, val) =>
    setDraftItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  const removeItem = (idx) =>
    setDraftItems(prev => prev.filter((_, i) => i !== idx));
  const addProduct = (p) => {
    setDraftItems(prev => {
      const existing = prev.findIndex(it => it.product_id === p.id);
      if (existing >= 0) {
        return prev.map((it, i) => i === existing ? { ...it, quantity: it.quantity + 1 } : it);
      }
      return [...prev, { product_id: p.id, name: p.name, image_url: p.image_url, price: p.discount_price || p.price, quantity: 1 }];
    });
    setProductSearch(''); setShowPicker(false);
  };

  const printShipping = () => {
    if (!order) return;
    const customer = order.user?.full_name || order.user?.username || order.guest_name || '—';
    const email    = order.user?.email || order.guest_email || '';
    const total    = order.total_amount + order.shipping_amount;
    const date     = new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Karachi' });
    const html = `<!DOCTYPE html><html><head><title>Shipping Label #${order.id}</title><style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:Arial,sans-serif; font-size:13px; color:#111; }
      .slip { max-width:680px; margin:0 auto; padding:32px; border:2px solid #111; }
      .slip-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; padding-bottom:16px; border-bottom:1px solid #ddd; }
      .store-name { font-size:22px; font-weight:800; } .store-name span { color:#f0b429; }
      .order-meta { text-align:right; } .order-num { font-size:20px; font-weight:700; } .order-date { font-size:11px; color:#666; margin-top:2px; }
      .section { margin-bottom:20px; }
      .section-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#888; margin-bottom:8px; }
      .ship-to { font-size:15px; line-height:1.6; } .ship-to .name { font-size:17px; font-weight:700; margin-bottom:4px; }
      table { width:100%; border-collapse:collapse; }
      th { font-size:11px; font-weight:700; text-transform:uppercase; color:#666; padding:6px 8px; border-bottom:1px solid #ddd; text-align:left; }
      td { padding:7px 8px; border-bottom:1px solid #f0f0f0; }
      .right { text-align:right; }
      .totals { margin-top:12px; border-top:2px solid #111; padding-top:10px; }
      .total-row { display:flex; justify-content:space-between; padding:3px 8px; }
      .grand { font-size:16px; font-weight:700; margin-top:6px; border-top:1px solid #ddd; padding-top:8px; }
    </style></head><body><div class="slip">
      <div class="slip-header">
        <div class="store-name">Seoul Station</div>
        <div class="order-meta"><div class="order-num">Order #${order.id}</div><div class="order-date">${date}</div></div>
      </div>
      <div class="section">
        <div class="section-title">Ship To</div>
        <div class="ship-to">
          <div class="name">${customer}</div>
          ${order.phone ? `<div>${order.phone}</div>` : ''}
          ${email       ? `<div>${email}</div>`       : ''}
          ${order.delivery_address ? `<div>${order.delivery_address}</div>` : ''}
        </div>
      </div>
      <div class="section">
        <div class="section-title">Items</div>
        <table><thead><tr><th>Product</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr></thead>
        <tbody>${order.items.map(i => `<tr><td>${i.name}</td><td class="right">${i.quantity}</td><td class="right">Rs ${i.price.toFixed(0)}</td><td class="right">Rs ${(i.price*i.quantity).toFixed(0)}</td></tr>`).join('')}</tbody></table>
      </div>
      <div class="totals">
        <div class="total-row"><span>Subtotal</span><span>Rs ${order.total_amount.toFixed(0)}</span></div>
        <div class="total-row"><span>Shipping</span><span>Rs ${order.shipping_amount.toFixed(0)}</span></div>
        <div class="total-row grand"><span>Total</span><span>Rs ${total.toFixed(0)}</span></div>
      </div>
    </div></body></html>`;
    const win = window.open('', '_blank', 'width=750,height=900');
    win.document.write(html); win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  if (loading) return <div className="admin-loading"><div className="admin-spinner" />Loading order…</div>;
  if (!order)  return <div className="admin-empty">Order not found. <button className="link-btn" onClick={() => navigate('/orders')}>Go back</button></div>;

  const customer = order.user?.full_name || order.user?.username || order.guest_name || '—';
  const email    = order.user?.email || order.guest_email || '';
  const subtotal = editing
    ? draftItems.reduce((s, i) => s + i.price * i.quantity, 0)
    : order.total_amount;
  const shipping = editing ? Number(draft.shipping_amount) : order.shipping_amount;
  const total    = subtotal + shipping;
  const st       = STATUS_STYLE[order.status] || STATUS_STYLE.pending;
  const date     = new Date(order.created_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Karachi' });

  const field = (key, label, type = 'text', multiline = false) => (
    <div className="od-edit-field">
      <label className="od-edit-label">{label}</label>
      {multiline
        ? <textarea
            className="od-edit-input od-edit-textarea"
            value={draft[key]}
            onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
            rows={3}
          />
        : <input
            className="od-edit-input"
            type={type}
            value={draft[key]}
            onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
          />
      }
    </div>
  );

  return (
    <div className="od-page">

      {/* Header */}
      <div className="od-page-header">
        <button className="od-back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft size={16} /> Orders
        </button>
        <div className="od-page-title-row">
          <div className="od-page-title">
            <h2>Order <span>#{order.id}</span></h2>
            <span className="od-date">{date}</span>
          </div>
          <div className="od-page-actions">
            <button className="btn-admin btn-admin-outline" onClick={duplicateOrder}>
              <FiCopy size={14} /> Duplicate
            </button>
            {editing ? (
              <>
                <button className="btn-admin btn-admin-ghost" onClick={cancelEdit} disabled={saving}>
                  <FiX size={14} /> Cancel
                </button>
                <button className="btn-admin" onClick={saveEdit} disabled={saving}>
                  <FiCheck size={14} /> {saving ? 'Saving…' : 'Save changes'}
                </button>
              </>
            ) : (
              <>
                <button className="btn-admin btn-admin-outline" onClick={printShipping}>
                  <FiPrinter size={14} /> Print Shipping
                </button>
                {order.status === 'shipped' && order.postex_tracking && (
                  <a
                    className="btn-admin btn-admin-outline"
                    href={`http://cod.callcourier.com.pk/Booking/AfterSavePublic/${order.postex_tracking}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FiExternalLink size={14} /> Print CC Slip
                  </a>
                )}
                {order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' && (
                  <button className="btn-admin btn-admin-outline" onClick={startEdit}>
                    <FiEdit2 size={14} /> Edit Order
                  </button>
                )}
                {order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' && (
                  <button className="btn-admin btn-fulfill" onClick={openFulfillPage}>
                    <FiTruck size={14} /> Fulfill Order
                  </button>
                )}
              </>
            )}
            <div className="od-status-wrap">
              <span className="od-status-pill" style={{ background: st.bg, color: st.color }}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              {order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' ? (
                <select
                  className="select-sm"
                  value={order.status}
                  onChange={e => handleStatusChange(e.target.value)}
                  disabled={saving}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              ) : (
                <span className="od-status-locked" title="Status locked">🔒</span>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Body */}
      <div className="od-page-body">

        {/* Left — items + totals */}
        <div className="od-main">
          <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
              <p className="od-card-title">Items ordered</p>
            </div>
            <div className="od-items-list">
              {(editing ? draftItems : order.items).map((item, i) => (
                <div key={i} className="od-item-row">
                  <div className="od-item-img-wrap">
                    {item.image_url
                      ? <img src={item.image_url} alt={item.name} className="od-item-img" />
                      : <div className="od-item-img-placeholder">{item.name?.[0]?.toUpperCase()}</div>
                    }
                    {!editing && <span className="od-item-qty-badge">{item.quantity}</span>}
                  </div>
                  <div className="od-item-details">
                    <p className="od-item-name">{item.name}</p>
                    {editing ? (
                      <div className="od-item-edit-fields">
                        <div className="od-item-edit-field">
                          <label>Price (Rs)</label>
                          <input type="number" min="0" step="1" className="od-edit-input od-item-num-input"
                            value={item.price} onChange={e => updItem(i, 'price', parseFloat(e.target.value)||0)} />
                        </div>
                        <div className="od-item-edit-field">
                          <label>Qty</label>
                          <div className="od-qty-ctrl">
                            <button onClick={() => updItem(i, 'quantity', Math.max(1, item.quantity - 1))}><FiMinus size={11}/></button>
                            <input type="number" min="1" className="od-edit-input od-item-qty-input"
                              value={item.quantity} onChange={e => updItem(i, 'quantity', Math.max(1, parseInt(e.target.value)||1))} />
                            <button onClick={() => updItem(i, 'quantity', item.quantity + 1)}><FiPlus size={11}/></button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="od-item-unit">Rs {item.price.toFixed(0)} each</p>
                    )}
                  </div>
                  {editing ? (
                    <div className="od-item-edit-right">
                      <span className="od-item-line-total">Rs {(item.price * item.quantity).toFixed(0)}</span>
                      <button className="od-item-remove-btn" onClick={() => removeItem(i)} title="Remove"><FiTrash2 size={13}/></button>
                    </div>
                  ) : (
                    <p className="od-item-line-total">Rs {(item.price * item.quantity).toFixed(0)}</p>
                  )}
                </div>
              ))}
              {editing && (
                <div className="od-add-product-wrap" ref={pickerRef}>
                  <button className="od-add-product-btn" onClick={() => setShowPicker(v => !v)}>
                    <FiPlus size={14}/> Add Product
                  </button>
                  {showPicker && (
                    <div className="od-product-picker">
                      <input
                        autoFocus
                        className="od-picker-search"
                        placeholder="Search products…"
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                      />
                      <div className="od-picker-list">
                        {allProducts
                          .filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                          .slice(0, 8)
                          .map(p => (
                            <div key={p.id} className="od-picker-item" onClick={() => addProduct(p)}>
                              {p.image_url && <img src={p.image_url} alt={p.name} className="od-picker-img"/>}
                              <div className="od-picker-info">
                                <span className="od-picker-name">{p.name}</span>
                                <span className="od-picker-price">Rs {(p.discount_price||p.price).toFixed(0)}</span>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="od-summary-block">
              <div className="od-summary-row"><span>Subtotal</span><span>Rs {subtotal.toFixed(0)}</span></div>
              <div className="od-summary-row">
                <span>Shipping</span>
                {editing
                  ? <input
                      className="od-edit-input od-edit-inline-num"
                      type="number"
                      min="0"
                      value={draft.shipping_amount}
                      onChange={e => setDraft(d => ({ ...d, shipping_amount: e.target.value }))}
                    />
                  : <span>Rs {shipping.toFixed(0)}</span>
                }
              </div>
              <div className="od-summary-row od-summary-total"><span>Total</span><span>Rs {total.toFixed(0)}</span></div>
            </div>
          </div>
        </div>

        {/* Right — customer + address */}
        <div className="od-sidebar">
          <div className="admin-card od-info-card">
            <p className="od-card-title">Customer</p>
            {editing && !order.user ? (
              <>
                {field('guest_name',  'Name')}
                {field('guest_email', 'Email', 'email')}
              </>
            ) : (
              <>
                <p className="od-customer-name">{customer}</p>
                {email && <p className="od-info-row"><FiMail size={13} />{email}</p>}
              </>
            )}
          </div>

          <div className="admin-card od-info-card">
            <p className="od-card-title">Shipping address</p>
            {editing ? (
              <>
                {field('phone',            'Phone',   'tel')}
                {field('delivery_address', 'Address', 'text', true)}
              </>
            ) : (
              <>
                {order.phone && <p className="od-info-row"><FiPhone size={13} />{order.phone}</p>}
                <p className="od-info-row od-addr-text"><FiMapPin size={13} />{order.delivery_address || '—'}</p>
              </>
            )}
          </div>

          <div className="admin-card od-info-card">
            <p className="od-card-title">Payment</p>
            <p className="od-info-row" style={{ color: '#854d0e', fontWeight: 600 }}>
              {order.status === 'delivered' ? '✓ Paid' : '○ Payment pending (COD)'}
            </p>
          </div>

          {order.postex_tracking && (
            <div className="admin-card od-info-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <p className="od-card-title" style={{ margin: 0 }}>Tracking — {order.postex_tracking}</p>
                <button
                  className="od-track-refresh"
                  onClick={() => fetchTracking(order.id)}
                  disabled={trackLoading}
                  title="Refresh"
                >
                  <FiRefreshCw size={13} className={trackLoading ? 'spin' : ''} />
                </button>
              </div>
              {trackLoading ? (
                <p className="od-track-empty">Loading…</p>
              ) : tracking?.events?.length > 0 ? (
                <div className="od-track-list">
                  {tracking.events.map((e, i) => {
                    const desc = e.OperationDesc || e.ProcessDescForPortal || e.Status || e.Activity || '—';
                    const loc  = e.OperationCity || e.City || '';
                    const time = e.OperationDate || e.EventDate || e.Date || '';
                    return (
                      <div key={i} className="od-track-event">
                        <div className="od-track-dot" />
                        <div className="od-track-info">
                          <p className="od-track-desc">{desc}{loc ? ` — ${loc}` : ''}</p>
                          {time && <p className="od-track-time">{time}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="od-track-empty">No tracking events yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Customer order history — full width below */}
      {order.customer_orders && order.customer_orders.length > 0 && (
        <div style={{ padding: '0 24px 24px' }}>
          <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="od-ch-table-header">
              <div>
                <p className="od-card-title" style={{ margin: 0 }}>Customer Order History</p>
                {order.customer_email && <p className="od-ch-email" style={{ margin: '2px 0 0' }}>{order.customer_email}</p>}
              </div>
              <span className="od-ch-count">{order.customer_orders.length} order{order.customer_orders.length !== 1 ? 's' : ''}</span>
            </div>
            <table className="od-ch-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {order.customer_orders.map(co => (
                  <tr
                    key={co.id}
                    className={co.id === order.id ? 'od-ch-tr-current' : 'od-ch-tr-link'}
                    onClick={() => co.id !== order.id && navigate(`/orders/${co.id}`)}
                  >
                    <td>
                      <span className="od-ch-order-num">#{co.id}</span>
                      {co.id === order.id && <span className="od-ch-current-tag">current</span>}
                    </td>
                    <td>{co.items_count} item{co.items_count !== 1 ? 's' : ''}</td>
                    <td className="od-ch-amount">Rs {parseFloat(co.total_amount).toFixed(0)}</td>
                    <td><span className={`od-ch-status od-ch-s-${co.status}`}>{co.status}</span></td>
                    <td className="od-ch-date">{new Date(co.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Asia/Karachi' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Comments */}
      <div style={{ padding: '0 24px 24px' }}>
        <div className="admin-card od-comments-card">
          <p className="od-card-title" style={{ marginBottom: 16 }}>Comments</p>

          {comments.length === 0 && (
            <p className="od-comments-empty">No comments yet.</p>
          )}

          {comments.length > 0 && (
            <div className="od-comments-list">
              {comments.map(c => (
                <div key={c.id} className="od-comment">
                  <div className="od-comment-dot" />
                  <div className="od-comment-body">
                    <div className="od-comment-meta">
                      <span className="od-comment-author">{c.author}</span>
                      <span className="od-comment-time">
                        {new Date(c.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Karachi' })}
                      </span>
                      <div className="od-comment-actions">
                        <button className="od-comment-btn" onClick={() => setEditingComment({ id: c.id, text: c.text })} title="Edit">
                          <FiEdit2 size={12} />
                        </button>
                        <button className="od-comment-btn od-comment-btn-del" onClick={() => deleteComment(c.id)} title="Delete">
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    </div>
                    {editingComment?.id === c.id ? (
                      <div className="od-comment-edit-row">
                        <textarea
                          className="od-comment-input"
                          rows={2}
                          value={editingComment.text}
                          onChange={e => setEditingComment(ec => ({ ...ec, text: e.target.value }))}
                          autoFocus
                        />
                        <div className="od-comment-edit-btns">
                          <button className="btn-admin btn-admin-primary btn-admin-sm" onClick={saveEditComment}>
                            <FiCheck size={12} /> Save
                          </button>
                          <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={() => setEditingComment(null)}>
                            <FiX size={12} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="od-comment-text">{c.text}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="od-comment-input-row">
            <textarea
              className="od-comment-input"
              placeholder="Add a comment…"
              rows={2}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) submitComment(); }}
            />
            <button
              className="btn-admin btn-admin-primary"
              onClick={submitComment}
              disabled={commentSaving || !commentText.trim()}
            >
              {commentSaving ? 'Saving…' : 'Add Comment'}
            </button>
          </div>
        </div>
      </div>

      {/* Cancel modal */}
      {cancelModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCancelModal(false)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>Cancel Order #{order.id}</h3>
              <button className="modal-close" onClick={() => setCancelModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '.88rem', marginBottom: 12 }}>
                This order will be moved to Cancelled. You can optionally add a reason.
              </p>
              <div className="admin-form-group">
                <label>Cancellation Reason (optional)</label>
                <textarea
                  rows={3}
                  placeholder="e.g. Customer requested cancellation, item out of stock…"
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-admin btn-admin-ghost" onClick={() => setCancelModal(false)} disabled={saving}>
                Keep Order
              </button>
              <button className="btn-admin btn-admin-danger" onClick={handleCancelOrder} disabled={saving}>
                {saving ? 'Cancelling…' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminOrderDetail;
